import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";

/**
 * Loads and renders PDF pages into canvases and extracts link annotations.
 * Mobile devices get a memory-friendly, windowed renderer.
 *
 * Returns { pages, linksByPage, totalPages, loading }
 */
export default function usePdfPages({
  srcUrl,
  pageWidth,
  quality = 1,
  onError,
  // Optional hints (the viewer may pass these); they are applied ONLY on mobile.
  currentIndex = 0,
  visibleRadius,
  maxPagesInMemory,
  dprCap,
}) {
  const [pages, setPages] = useState([]);
  const [linksByPage, setLinksByPage] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  // Track the latest center index to avoid stale closures
  const centerRef = useRef(currentIndex);
  useEffect(() => { centerRef.current = currentIndex; }, [currentIndex]);

  // NEW: expose renderWindow + total to other effects
  const renderWindowRef = useRef(null);
  const totalRef = useRef(0);
  
  // Track cleanup promise to ensure sequential initialization
  const cleanupPromiseRef = useRef(Promise.resolve());

  // Detect mobile (coarse pointer or mobile UA)
  const isCoarse =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  const isMobileUA =
    typeof navigator !== "undefined" &&
    /Mobi|Android/i.test(navigator.userAgent || "");
  const isMobile = isCoarse || isMobileUA;

  const MOBILE_RADIUS_DEFAULT = 1;
  const MOBILE_RADIUS_MAX = 2;
  const MOBILE_MAX_PAGES_DEFAULT = 6;
  const MOBILE_MAX_PAGES_HARD = 8;
  const MOBILE_DPR_FLOOR = 2.0;
  const MOBILE_DPR_CEIL = 3.2;
  const MOBILE_PRIMARY_DISTANCE = 0;
  const MOBILE_PRIMARY_QUALITY_BOOST = 1.5;
  const MOBILE_SECONDARY_QUALITY_SCALE = 1.15;

  // Effective mobile-only knobs
  const effRadius = isMobile
    ? Math.max(
        1,
        Math.min(
          Number.isFinite(visibleRadius) ? visibleRadius : MOBILE_RADIUS_DEFAULT,
          MOBILE_RADIUS_MAX
        )
      )
    : Infinity;
  const effMaxPages = isMobile
    ? Math.max(
        4,
        Math.min(
          Number.isFinite(maxPagesInMemory) ? maxPagesInMemory : MOBILE_MAX_PAGES_DEFAULT,
          MOBILE_MAX_PAGES_HARD
        )
      )
    : Infinity;
  const effDprCap = isMobile
    ? Math.min(
        Math.max(Number.isFinite(dprCap) ? dprCap : MOBILE_DPR_CEIL, MOBILE_DPR_FLOOR),
        3.25
      )
    : 3;

  useEffect(() => {
    let cancelled = false;
    let doc = null;
    let loadingTask = null; // Track the PDF loading task for proper cleanup
    const abortController = new AbortController(); // Track abort signal for fetch streams

    // Local mutable caches so we can edit without triggering renders every mutation
    let pageCanvases = [];
    let pageLinks = [];
    const inFlight = new Map(); // pageNumber -> Promise

    const releaseCanvas = (entry) => {
      if (!entry) return;
      const canvas = entry.canvas || entry;
      if (canvas && typeof canvas.width === "number") {
        try {
          canvas.width = 0;
          canvas.height = 0;
        } catch (err) {
          // ignore
        }
      }
    };

    const commit = () => {
      setPages([...pageCanvases]);
      setLinksByPage([...pageLinks]);
    };

    const evictIfNeeded = (center) => {
      if (!Number.isFinite(effMaxPages) || effMaxPages === Infinity) return;
      let materialized = 0;
      for (let i = 0; i < pageCanvases.length; i++) {
        if (pageCanvases[i]) materialized++;
      }
      if (materialized <= effMaxPages) return;

      const toEvict = [];
      for (let i = 0; i < pageCanvases.length; i++) {
        if (pageCanvases[i]) {
          toEvict.push({ i, dist: Math.abs(i - center) });
        }
      }
      toEvict.sort((a, b) => b.dist - a.dist);

      let idx = 0;
      while (materialized > effMaxPages && idx < toEvict.length) {
        const i = toEvict[idx].i;
        if (Math.abs(i - center) > effRadius) {
          if (pageCanvases[i]) releaseCanvas(pageCanvases[i]);
          pageCanvases[i] = null; // keep links
          materialized--;
        }
        idx++;
      }
    };

    const renderOne = async (n) => {
      if (cancelled || !doc) return null;
      if (inFlight.has(n)) return inFlight.get(n);

      const zeroIndex = n - 1;
      const center = centerRef.current || 0;
      const distance = Math.abs(zeroIndex - center);
      const isPrimaryFocus = isMobile && distance <= MOBILE_PRIMARY_DISTANCE;
      const qualityTag = isMobile ? (isPrimaryFocus ? "primary" : "secondary") : "desktop";

      const p = (async () => {
        let page = null;
        try {
          if (cancelled) return null;

          page = await doc.getPage(n);
          if (cancelled) return null;

          const v1 = page.getViewport({ scale: 1 });
          const rawDpr =
            typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
          const dpr = Math.min(Math.max(rawDpr, 1), effDprCap);
          const baseQuality = Math.max(1, quality || 1);
          const qualityMultiplier = isMobile
            ? (isPrimaryFocus ? MOBILE_PRIMARY_QUALITY_BOOST : MOBILE_SECONDARY_QUALITY_SCALE)
            : 1;
          const effectiveQuality = Math.max(1, baseQuality * qualityMultiplier);
          const scale = (pageWidth / v1.width) * dpr * effectiveQuality;
          const viewport = page.getViewport({ scale });

          const off = document.createElement("canvas");
          off.width = Math.round(viewport.width);
          off.height = Math.round(viewport.height);
          const ctx = off.getContext("2d", { alpha: false, willReadFrequently: false });
          if (ctx) {
            ctx.imageSmoothingEnabled = false;
            ctx.imageSmoothingQuality = "high";
          }

          if (cancelled) return null;

          await page.render({ canvasContext: ctx, viewport }).promise;

          if (cancelled) return null;

          // --- Extract embedded link annotations ---
          const annots = await page.getAnnotations();
          if (cancelled) return null;

          const links = [];

          const pushBox = (x, y, w, h, url, title = "") => {
            const vw = viewport.width || off.width || 1;
            const vh = viewport.height || off.height || 1;
            const leftPct = x / vw;
            const topPct = y / vh;
            const widthPct = w / vw;
            const heightPct = h / vh;
            links.push({ x, y, w, h, leftPct, topPct, widthPct, heightPct, url, title });
          };

          for (const a of annots) {
            if (a.subtype !== "Link") continue;
            const url = a.url || a.unsafeUrl;
            if (!url) continue;

            const qp = a.quadPoints || a.quadrilaterals || a.quads;
            if (Array.isArray(qp) && qp.length >= 8) {
              for (let i = 0; i + 7 < qp.length; i += 8) {
                const x1y1 = viewport.convertToViewportPoint(qp[i + 0], qp[i + 1]);
                const x2y2 = viewport.convertToViewportPoint(qp[i + 2], qp[i + 3]);
                const x3y3 = viewport.convertToViewportPoint(qp[i + 4], qp[i + 5]);
                const x4y4 = viewport.convertToViewportPoint(qp[i + 6], qp[i + 7]);
                const xs = [x1y1[0], x2y2[0], x3y3[0], x4y4[0]];
                const ys = [x1y1[1], x2y2[1], x3y3[1], x4y4[1]];
                const x = Math.min(...xs);
                const y = Math.min(...ys);
                const w = Math.max(1, Math.max(...xs) - x);
                const h = Math.max(1, Math.max(...ys) - y);
                pushBox(x, y, w, h, url, a.title || a.contents || "");
              }
            } else if (a.rect) {
              const [vx1, vy1, vx2, vy2] = viewport.convertToViewportRectangle(a.rect);
              const x = Math.min(vx1, vx2);
              const y = Math.min(vy1, vy2);
              const w = Math.abs(vx2 - vx1);
              const h = Math.abs(vy2 - vy1);
              pushBox(x, y, w, h, url, a.title || a.contents || "");
            }
          }

          // --- Detect text-based URLs/emails (Chrome-like heuristic) ---
          if (cancelled) return null;

          const textContent = await page.getTextContent();
          if (cancelled) return null;

          // eslint-disable-next-line no-useless-escape
          const urlPattern = /https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+|\b[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"{}|\\^`[\]()]*/gi;
          const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

          for (const item of textContent.items) {
            if (!item?.str) continue;

            const urlMatches = [...item.str.matchAll(urlPattern)];
            const emailMatches = [...item.str.matchAll(emailPattern)];
            if (urlMatches.length === 0 && emailMatches.length === 0) continue;

            const tx = item.transform;
            if (!tx || tx.length < 6) continue;
            const [, , , , x, y] = tx;
            const w = item.width || 100;
            const h = item.height || 12;

            const [vx, vy] = viewport.convertToViewportPoint(x, y);
            const [vx2, vy2] = viewport.convertToViewportPoint(x + w, y + h);
            const baseX = Math.min(vx, vx2);
            const baseY = Math.min(vy, vy2);
            const vw = Math.abs(vx2 - vx);
            const vh = Math.abs(vy2 - vy);

            const totalChars = item.str.length || 1;
            const toSubRect = (startIdx, len) => {
              const clampedStart = Math.max(0, Math.min(totalChars, startIdx));
              const clampedEnd = Math.max(clampedStart, Math.min(totalChars, clampedStart + len));
              const fracStart = clampedStart / totalChars;
              const fracEnd = clampedEnd / totalChars;
              const subX = baseX + vw * fracStart;
              const subW = Math.max(1, vw * (fracEnd - fracStart));
              return { subX, subW };
            };
            const trimTrailingPunct = (s) => s.replace(/[).,;:]+$/g, "");

            for (const match of urlMatches) {
              const raw = match[0];
              const visual = trimTrailingPunct(raw);
              let url = visual;
              if (!/^https?:\/\//i.test(url)) url = "http://" + url;
              const start = match.index ?? 0;
              const len = visual.length;
              const { subX, subW } = toSubRect(start, len);
              links.push({
                x: subX,
                y: baseY,
                w: subW,
                h: vh,
                leftPct: subX / viewport.width,
                topPct: baseY / viewport.height,
                widthPct: subW / viewport.width,
                heightPct: vh / viewport.height,
                url,
                title: visual,
              });
            }

            for (const match of emailMatches) {
              const email = match[0];
              const start = match.index ?? 0;
              const len = email.length;
              const { subX, subW } = toSubRect(start, len);
              const mailtoUrl = "mailto:" + email;
              links.push({
                x: subX,
                y: baseY,
                w: subW,
                h: vh,
                leftPct: subX / viewport.width,
                topPct: baseY / viewport.height,
                widthPct: subW / viewport.width,
                heightPct: vh / viewport.height,
                url: mailtoUrl,
                title: email,
              });
            }
          }

          return { canvas: off, width: off.width, height: off.height, links, qualityTag };
        } catch (err) {
          if (cancelled || err?.name === "AbortError" || err?.name === "AbortException") {
            return null;
          }
          throw err;
        } finally {
          try {
            page?.cleanup?.();
          } catch (cleanupErr) {
            // ignore cleanup issues
          }
        }
      })();

      inFlight.set(n, p);
      const result = await p.finally(() => inFlight.delete(n));
      return result;
    };

    const renderAndStore = async (nZeroBased) => {
      if (cancelled) return;
      const n = nZeroBased + 1; // pdf.js pages are 1-based
      const center = centerRef.current || 0;
      const isPrimaryFocus = isMobile && Math.abs(nZeroBased - center) <= MOBILE_PRIMARY_DISTANCE;
      const existing = pageCanvases[nZeroBased];

      if (existing) {
        if (!isMobile) return;
        const tag = existing.qualityTag || "secondary";
        if (isPrimaryFocus && tag !== "primary") {
          releaseCanvas(existing);
          pageCanvases[nZeroBased] = null;
        } else {
          return;
        }
      }

      const out = await renderOne(n);
      if (!out || cancelled) return;
      pageCanvases[nZeroBased] = {
        canvas: out.canvas,
        width: out.width,
        height: out.height,
        qualityTag: out.qualityTag,
      };
      pageLinks[nZeroBased] = out.links;
      commit();
    };

    const renderWindow = async (center, total) => {
      if (!Number.isFinite(effRadius) || effRadius === Infinity) return;
      const start = Math.max(0, center - effRadius);
      const end = Math.min(total - 1, center + effRadius);

      const order = [];
      for (let r = 0; start + r <= end; r++) {
        const left = center - r;
        const right = center + r;
        if (left >= start) order.push(left);
        if (right !== left && right <= end) order.push(right);
      }
      for (const i of order) {
        if (cancelled) return;
        await renderAndStore(i);
      }
      evictIfNeeded(center);
      commit();
    };

    // EXPOSE renderWindow to the outer effect
    renderWindowRef.current = renderWindow;

    async function run() {
      if (!srcUrl) return;
      
      // Wait for any pending cleanup from previous effect to complete
      // This prevents "worker is being destroyed" race conditions
      try {
        await cleanupPromiseRef.current;
      } catch (err) {
        // Ignore cleanup errors
      }
      if (cancelled) return;
      
      setLoading(true);
      setPages([]); setLinksByPage([]); setTotalPages(0);

      try {
        // Pass abort signal to prevent "signal is aborted without reason" errors
        loadingTask = pdfjs.getDocument({ 
          url: srcUrl,
          stopAtErrors: true,
          // Use abort controller to provide proper cancellation reason
          abortSignal: abortController.signal
        });
        doc = await loadingTask.promise;
        if (cancelled) return;

        const total = doc.numPages;
        setTotalPages(total);
        totalRef.current = total;               // <-- RIGHT AFTER setTotalPages
        pageCanvases = new Array(total).fill(null);
        pageLinks = new Array(total).fill(null);

        if (!isMobile) {
          const firstBatch = Math.min(2, total);
          for (let i = 0; i < firstBatch; i++) {
            if (cancelled) return;
            await renderAndStore(i);
          }
          setLoading(false);
          for (let i = firstBatch; i < total; i++) {
            if (cancelled) return;
            await renderAndStore(i);
          }
          return;
        }

        // Mobile: render a small window fast, then expand as user flips
        const initialCenter = Math.min(Math.max(0, centerRef.current || 0), total - 1);
        await renderAndStore(initialCenter);
        if (initialCenter + 1 < total) await renderAndStore(initialCenter + 1);
        setLoading(false);

        // Fill the rest of the initial window (use local total)
        await renderWindow(initialCenter, total);
      } catch (e) {
        // Ignore abort errors (these are expected when component unmounts)
        if (e?.name === 'AbortError' || cancelled) {
          return;
        }
        // Log other errors
        console.error("PDF render failed", e);
        onError?.(e);
        setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
      
      // Store cleanup promise so next effect can wait for it
      cleanupPromiseRef.current = (async () => {
        // Step 1: Abort the controller to signal all fetch operations
        try {
          abortController.abort('Component unmounted or PDF source changed');
        } catch (err) {
          // AbortController may already be aborted
        }
        
        // Step 2: Wait for all in-flight render operations to complete or cancel
        // This is critical - we need to let ongoing page.render() calls finish
        // before destroying the document
        const pendingOps = Array.from(inFlight.values());
        if (pendingOps.length > 0) {
          await Promise.allSettled(pendingOps);
        }
        
        // Step 3: Give a small buffer for any lingering async callbacks
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Step 4: Destroy loading task (if it exists and hasn't been destroyed)
        if (loadingTask) {
          try {
            await loadingTask.destroy();
          } catch (err) {
            // Silently ignore - task may already be destroyed
          }
          loadingTask = null;
        }
        
        // Step 5: Release any materialized canvases
        try {
          if (pageCanvases && pageCanvases.length) {
            for (const entry of pageCanvases) {
              releaseCanvas(entry);
            }
          }
          pageCanvases = [];
        } catch (err) {
          // ignore release issues
        }

        // Step 6: Finally cleanup the document
        if (doc) {
          try {
            await doc.destroy();
          } catch (err) {
            // Silently ignore - document may already be destroyed
          }
          doc = null;
        }
      })().catch(() => {
        // Ignore any errors in the cleanup chain
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcUrl, pageWidth, quality, onError, isMobile, effRadius, effMaxPages, effDprCap]);

  // NEW: React to currentIndex changes on mobile and render around the new center.
  useEffect(() => {
    if (!isMobile) return;
    const fn = renderWindowRef.current;
    const total = totalRef.current;
    if (!fn || !total) return;
    const center = Math.min(Math.max(0, centerRef.current || 0), total - 1);
    const id = requestAnimationFrame(() => { fn(center, total); });
    return () => cancelAnimationFrame(id);
  }, [currentIndex, isMobile, effRadius]);

  return { pages, linksByPage, totalPages, loading };
}
